import {formatMbpsCapacity} from "./helpers/scaling";

const DEFAULT_PAGE_SIZE = 25;

function debounce(fn, wait) {
    let timeout = null;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), wait);
    };
}

function safeTrim(value) {
    return value ? String(value).trim() : "";
}

export function initCircuitsPage(config) {
    const listEl = $(config.listSelector || "#circuitsList");
    const infoEl = $(config.infoSelector || "#pageInfo");
    const prevBtn = $(config.prevSelector || "#prevPage");
    const nextBtn = $(config.nextSelector || "#nextPage");
    const shaperSelect = $(config.shaperSelector || "#circuitShaperSelect");
    const siteSelect = $(config.siteSelector || "#circuitSiteFilter");
    const searchInput = $(config.searchSelector || "#circuitSearch");
    const searchBtn = $(config.searchButtonSelector || "#searchBtn");
    const helperEl = $(config.helperSelector || "#circuitsHelper");

    const pageSize = config.pageSize || DEFAULT_PAGE_SIZE;

    const state = {
        nodeId: config.initialNodeId || "",
        currentPage: 1,
        totalPages: 1,
        searchTerm: "",
        siteHash: "",
        shapers: [],
        sites: [],
    };

    function setHelperText(text) {
        if (helperEl.length > 0) {
            helperEl.text(text);
        }
    }

    function renderSiteOptions(sites) {
        const previous = state.siteHash;
        state.sites = Array.isArray(sites) ? sites.slice() : [];
        siteSelect.empty();

        const allOption = document.createElement("option");
        allOption.value = "";
        allOption.textContent = "All destination sites";
        siteSelect.append(allOption);

        state.sites.forEach((site) => {
            const option = document.createElement("option");
            option.value = site.site_hash;
            option.textContent = `${site.site_name} (${site.circuit_count})`;
            siteSelect.append(option);
        });

        if (previous && state.sites.some((site) => site.site_hash === previous)) {
            siteSelect.val(previous);
            state.siteHash = previous;
        } else {
            siteSelect.val("");
            state.siteHash = "";
        }
    }

    function renderCircuits(data) {
        listEl.empty();
        if (!data.circuits || data.circuits.length === 0) {
            const details = [];
            if (state.siteHash) {
                const matchedSite = state.sites.find((site) => site.site_hash === state.siteHash);
                if (matchedSite) {
                    details.push(`site "${matchedSite.site_name}"`);
                }
            }
            if (state.searchTerm) {
                details.push("search filter");
            }
            const suffix = details.length > 0 ? ` for ${details.join(" and ")}` : "";
            listEl.append(
                `<div class="col-12"><div class="alert alert-secondary">No circuits found on this shaper${suffix}.</div></div>`
            );
            return;
        }

        data.circuits.forEach((circuit) => {
            const col = document.createElement("div");
            col.className = "col-12 col-md-6 col-xl-4";

            const card = document.createElement("div");
            card.className = "card h-100 shadow-sm";

            const body = document.createElement("div");
            body.className = "card-body d-flex flex-column gap-3";

            const header = document.createElement("div");
            header.className = "d-flex justify-content-between align-items-start gap-3 flex-wrap";

            const title = document.createElement("div");
            title.className = "flex-grow-1 min-w-0";

            const name = document.createElement("div");
            name.className = "fw-bold redactable text-truncate";
            name.textContent = circuit.circuit_name;

            const subtitle = document.createElement("div");
            subtitle.className = "text-muted small";
            subtitle.textContent = circuit.circuit_id || "Unknown ID";

            const siteLine = document.createElement("div");
            siteLine.className = "d-flex flex-wrap align-items-center gap-2 text-muted small";
            const siteBadge = document.createElement("span");
            siteBadge.className = "badge text-bg-secondary";
            siteBadge.textContent = "Site";
            siteLine.appendChild(siteBadge);
            const siteName = document.createElement("span");
            siteName.className = "redactable";
            siteName.textContent = circuit.site_name || "Unassigned";
            siteLine.appendChild(siteName);

            const shaperLine = document.createElement("div");
            shaperLine.className = "text-muted small";
            shaperLine.textContent = circuit.shaper_name;

            title.appendChild(name);
            title.appendChild(subtitle);
            title.appendChild(siteLine);
            title.appendChild(shaperLine);

            const actions = document.createElement("div");
            actions.className = "text-end";

            const dl = document.createElement("div");
            dl.innerHTML = `<i class="fa fa-download me-1 text-success"></i> ${formatMbpsCapacity(circuit.download_max_mbps, 1)}`;

            const ul = document.createElement("div");
            ul.innerHTML = `<i class="fa fa-upload me-1 text-primary"></i> ${formatMbpsCapacity(circuit.upload_max_mbps, 1)}`;

            const link = document.createElement("a");
            link.className = "btn btn-sm btn-primary mt-2";
            const circuitHash = String(circuit.circuit_hash || "");
            link.href = `shaper_circuit.html?circuit=${encodeURIComponent(circuitHash)}&nodeId=${encodeURIComponent(circuit.shaper_node_id)}`;
            link.textContent = "View circuit";

            actions.appendChild(dl);
            actions.appendChild(ul);
            actions.appendChild(link);

            header.appendChild(title);
            header.appendChild(actions);

            body.appendChild(header);
            card.appendChild(body);
            col.appendChild(card);
            listEl.append(col);
        });
    }

    function updatePagination(data) {
        const total = data.total || 0;
        state.totalPages = Math.max(1, Math.ceil(total / data.page_size));
        infoEl.text(`Page ${data.page} of ${state.totalPages} • ${total} circuit${total === 1 ? "" : "s"}`);
        prevBtn.prop("disabled", data.page <= 1);
        nextBtn.prop("disabled", data.page >= state.totalPages);
    }

    async function fetchCircuits() {
        if (!state.nodeId) {
            listEl.html(`<div class="col-12"><div class="alert alert-warning">Select a shaper to see circuits.</div></div>`);
            setHelperText("Pick a shaper to start.");
            siteSelect.prop("disabled", true);
            return;
        }

        listEl.html("<div class='col-12'><div class='text-center text-muted py-3'><i class='fa fa-spinner fa-spin'></i> Loading circuits…</div></div>");
        siteSelect.prop("disabled", true);

        try {
            const data = await config.loadCircuits({
                nodeId: state.nodeId,
                page: state.currentPage,
                pageSize,
                search: state.searchTerm,
                siteHash: state.siteHash,
            });

            renderSiteOptions(data.sites || []);
            siteSelect.prop("disabled", state.sites.length === 0);
            renderCircuits(data);
            updatePagination(data);
            setHelperText("Search by customer/device ID, circuit ID, or circuit name. Use the site filter to stay on this page and narrow the current list.");

            if (typeof config.onCircuitsLoaded === "function") {
                config.onCircuitsLoaded(state.nodeId, data);
            }
        } catch (err) {
            listEl.html(`<div class="col-12"><div class="alert alert-danger">Unable to load circuits right now.</div></div>`);
            setHelperText("The circuits list could not be loaded.");
        }
    }

    async function populateShaperSelector() {
        try {
            state.shapers = await config.loadShapers();
        } catch (err) {
            state.shapers = [];
        }

        if (typeof config.resolveNodeId === "function") {
            state.nodeId = safeTrim(config.resolveNodeId(state.nodeId, state.shapers));
        }

        shaperSelect.empty();
        state.shapers.forEach((shaper) => {
            const option = document.createElement("option");
            option.value = shaper.node_id;
            option.text = shaper.node_name + (shaper.role ? ` (${shaper.role})` : "");
            shaperSelect.append(option);
        });

        if (!state.nodeId && state.shapers.length > 0) {
            state.nodeId = state.shapers[0].node_id;
        }

        if (state.nodeId) {
            shaperSelect.val(state.nodeId);
        }

        await fetchCircuits();
    }

    shaperSelect.on("change", async () => {
        state.nodeId = safeTrim(shaperSelect.val());
        state.currentPage = 1;
        state.siteHash = "";
        await fetchCircuits();
    });

    siteSelect.on("change", async () => {
        state.siteHash = safeTrim(siteSelect.val());
        state.currentPage = 1;
        await fetchCircuits();
    });

    searchBtn.on("click", async () => {
        state.searchTerm = safeTrim(searchInput.val());
        state.currentPage = 1;
        await fetchCircuits();
    });

    searchInput.on(
        "input",
        debounce(async () => {
            state.searchTerm = safeTrim(searchInput.val());
            state.currentPage = 1;
            await fetchCircuits();
        }, 300)
    );

    prevBtn.on("click", async () => {
        if (state.currentPage > 1) {
            state.currentPage -= 1;
            await fetchCircuits();
        }
    });

    nextBtn.on("click", async () => {
        if (state.currentPage < state.totalPages) {
            state.currentPage += 1;
            await fetchCircuits();
        }
    });

    populateShaperSelector();
}
